let sessionToken: string | null = null;
let rateLimitedUntil = 0;
const sessionTokenListeners = new Set<() => void>();
const sessionExpiredListeners = new Set<() => void>();

export function setSessionToken(token: string | null): void {
  if (token !== sessionToken || token === null) rateLimitedUntil = 0;
  sessionToken = token;
  for (const listener of sessionTokenListeners) listener();
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('ponchik:session'));
}

export function getSessionToken(): string | null {
  return sessionToken;
}

export function subscribeSessionToken(listener: () => void): () => void {
  sessionTokenListeners.add(listener);
  return () => sessionTokenListeners.delete(listener);
}

export function subscribeSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

export class ApiClientError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly retryAfterSeconds?: number,
    public readonly status?: number,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  if (rateLimitedUntil > Date.now())
    throw new ApiClientError(
      'RATE_LIMITED',
      'Слишком много запросов. Сделайте короткую паузу.',
      undefined,
      Math.ceil((rateLimitedUntil - Date.now()) / 1_000),
      429,
    );
  const headers = new Headers(options.headers);
  const requestSession = sessionToken;
  headers.set('Accept', 'application/json');
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (sessionToken) headers.set('Authorization', `Bearer ${sessionToken}`);
  if (options.method && !['GET', 'HEAD'].includes(options.method)) {
    headers.set('Idempotency-Key', crypto.randomUUID());
  }
  const response = await fetch(`/api/v1${path}`, { ...options, headers, credentials: 'omit' });
  if (response.status === 204) return undefined as T;
  const body = (await response.json().catch(() => null)) as {
    data?: T;
    error?: { code: string; message: string; details?: { retryAfterSeconds?: number } };
    requestId?: string;
  } | null;
  if (!response.ok || !body || !('data' in body)) {
    const code = body?.error?.code ?? (response.status === 429 ? 'RATE_LIMITED' : 'NETWORK_ERROR');
    let retryAfterSeconds: number | undefined;
    if (response.status === 429 && code === 'RATE_LIMITED') {
      const raw = response.headers.get('Retry-After');
      const headerSeconds =
        raw === null
          ? NaN
          : /^\d+$/.test(raw)
            ? Number(raw)
            : (Date.parse(raw) - Date.now()) / 1_000;
      const seconds = Number.isFinite(headerSeconds)
        ? headerSeconds
        : body?.error?.details?.retryAfterSeconds;
      retryAfterSeconds = Math.max(
        1,
        Math.min(
          120,
          Math.ceil(typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : 10),
        ),
      );
      if (sessionToken === requestSession)
        rateLimitedUntil = Math.max(rateLimitedUntil, Date.now() + retryAfterSeconds * 1_000);
    }
    if (body?.error?.code === 'SESSION_EXPIRED') {
      setSessionToken(null);
      for (const listener of sessionExpiredListeners) listener();
    }
    throw new ApiClientError(
      code,
      body?.error?.message ?? 'Не удалось выполнить запрос',
      body?.requestId,
      retryAfterSeconds,
      response.status,
    );
  }
  return body.data;
}
